import type { LocationMatch } from '@citibox-panel/api-client';

// The server trims the term and then rejects it outside these bounds ([AS-037]).
const MIN_TERM = 3;
const MAX_TERM = 100;

// The matrícula the address search appends INSIDE a row when two ubicaciones would otherwise read
// identically (`… 28020 Madrid (BNB00)`). The ficha's line never carries it.
const MATRICULA_SUFFIX = /\s*\([a-z0-9]+\)\s*$/;

// What the address search is asked, to find the titular's ubicación: the address up to its first
// comma — street, number and portal, without the postal code and city — so the term does not depend
// on the read also matching those. Null when there is nothing the server would accept.
export function titularLocationQuery(address: string): string | null {
  const term = (address.split(',')[0] ?? '').trim().slice(0, MAX_TERM).trim();
  return term.length >= MIN_TERM ? term : null;
}

const fold = (text: string): string =>
  text
    .normalize('NFKD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(MATRICULA_SUFFIX, '')
    .replace(/\s+/g, ' ')
    .replace(/\s*,\s*/g, ', ')
    .trim();

// The ubicación the titular's stored address belongs to, among the rows the address search answered
// for it. The search returns one row per PORTAL, each under its ubicación's id, so the row that
// names the titular address WHOLE says which Citibox the titular belongs to — two portals of one
// building share an id, two buildings on one street number do not. The substring neighbours of the
// term (portal 30 for portal 3) are ignored, and so is any answer that does not come down to exactly
// one id: no row, or two ubicaciones naming the same line, leaves the caller without an identity.
export function titularLocationId(rows: LocationMatch[], titularAddress: string): number | null {
  const needle = fold(titularAddress);
  const ids = new Set(rows.filter((row) => fold(row.address) === needle).map((row) => row.id));
  return ids.size === 1 ? ([...ids][0] ?? null) : null;
}
